import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import * as authApi from "../api/auth.api.js";
import { onSessionEnded, setAccessToken } from "../api/client.js";
import type { Permission, Principal } from "../types/api.js";

interface AuthState {
  principal: Principal | null;
  /** True until the one-time refresh on app load has settled. Render a skeleton for this, not
   * the sign-in form: showing sign-in to a user who IS signed in is a visible flicker on every
   * page load. */
  restoring: boolean;
  endedReason: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [endedReason, setEndedReason] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Tell the API client how to report a session it could not renew, so a revoked credential
    // mid-session returns the user to sign-in with a reason rather than looping on errors.
    onSessionEnded((reason) => {
      if (cancelled) return;
      setPrincipal(null);
      setEndedReason(reason);
    });

    // One refresh before any route renders. Success means signed in; failure means not.
    void authApi
      .restoreSession()
      .then((session) => {
        if (cancelled) return;
        if (session) setPrincipal(session.principal);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await authApi.login(email, password);
    setPrincipal(session.principal);
    setEndedReason(null);
  }, []);

  const signOut = useCallback(async () => {
    await authApi.logout();
    setAccessToken(null);
    setPrincipal(null);
    setEndedReason(null);
  }, []);

  const can = useCallback(
    (permission: Permission) => principal?.permissions.includes(permission) ?? false,
    [principal],
  );

  const value = useMemo<AuthState>(
    () => ({ principal, restoring, endedReason, signIn, signOut, can }),
    [principal, restoring, endedReason, signIn, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside an AuthProvider");
  return ctx;
}
